import React, { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import { CategoryID } from '../../../../categories/categoriesTypes';
import { IconsEnum } from '../../../../../assets/icons/types';
import { ItemsFilters } from '../../../../../types';

import { useThreeDStockMaterialsCategories } from '../../../hooks/useThreeDStockMaterialsCategories';

import {
  ThreeDStockMenuCategoriesNav,
  ThreeDStockMenuCategoriesNavCategory
} from '../ThreeDStockMenuCategoriesNav';

import { PureIconButtonHelper } from '../../../../../helpers/buttons/PureIconButtonHelper';
import { AlertMessage } from '../../../../../helpers/AlertMessage';
import { LoadingSkeleton } from '../../../../../helpers/LoadingSkeleton';
import { NextPureLinkHelper } from '../../../../../helpers/links/NextPureLinkHelper';

import { getChangeMaterialsFiltersCategoriesData } from '../../../../materials/utils/getChangeMaterialsFiltersCategoriesData';

import { materialsKeys } from '../../../../../locales/keys';
import { StockTabs } from '../../../stockConstants';

interface ThreeDStockMenuMaterialsCategoriesNavProps {
  categoryTitleHref: string;
  onClose: () => void;
  onSelectCategory: (id: CategoryID) => void;
  changeItemsCategoryFilters?: (
    tab?: StockTabs,
    changeFilters?: ItemsFilters,
    removeFilters?: string[]
  ) => void;
  alwaysShowAllCategories?: boolean;
  disabledTitleLink?: boolean;
  clientIds?: string[];
}

function ThreeDStockMenuMaterialsCategoriesNav({
  categoryTitleHref,
  onClose,
  onSelectCategory,
  changeItemsCategoryFilters,
  alwaysShowAllCategories,
  disabledTitleLink,
  clientIds
}: ThreeDStockMenuMaterialsCategoriesNavProps) {
  const [currentCategory, setCurrentCategory] =
    useState<ThreeDStockMenuCategoriesNavCategory>(undefined);

  const materialsFilters = isEmpty(clientIds)
    ? {
        nda: false,
        blocked: false
      }
    : {
        blocked: false,
        clientIds
      };

  const {
    materialCategoriesWithParents,
    materialCategoriesErrorMessage,
    materialCategoriesFetched,
    materialCategoriesIsPlaceholderData
  } = useThreeDStockMaterialsCategories({
    materialsFilters
  });

  const handleChangeFilters = useCallback<
    (category: ThreeDStockMenuCategoriesNavCategory) => void
  >(
    (category) => {
      const { changeFilters, resetFilters } =
        getChangeMaterialsFiltersCategoriesData({
          materialCategory: category,
          categories: materialCategoriesWithParents
        });

      changeItemsCategoryFilters?.(
        StockTabs.MATERIALS,
        changeFilters,
        resetFilters
      );
    },
    [changeItemsCategoryFilters, materialCategoriesWithParents]
  );

  const handleSetCurrentCategory = useCallback<
    (parentCategory?: ThreeDStockMenuCategoriesNavCategory) => void
  >(
    (parentCategory) => {
      onSelectCategory(parentCategory?.id);
      setCurrentCategory(parentCategory);
    },
    [onSelectCategory]
  );

  const handleSelectCategory = useCallback<
    (id: CategoryID, hasChildrenCategories?: boolean) => void
  >(
    (id, hasChildrenCategories) => {
      const selectedCategory = materialCategoriesWithParents?.find(
        (category) => category.id === id
      );

      if (!hasChildrenCategories) {
        handleChangeFilters(selectedCategory);
        onClose();
        return;
      }

      handleSetCurrentCategory(selectedCategory);
    },
    [
      handleChangeFilters,
      handleSetCurrentCategory,
      materialCategoriesWithParents,
      onClose
    ]
  );

  const handleBack = useCallback<() => void>(() => {
    const parentCategory = currentCategory?.parent?.id
      ? materialCategoriesWithParents?.find(
          (category) => category.id === currentCategory?.parent?.id
        )
      : undefined;
    handleSetCurrentCategory(parentCategory);
  }, [
    currentCategory?.parent?.id,
    handleSetCurrentCategory,
    materialCategoriesWithParents
  ]);

  return (
    <div className="py-8">
      {currentCategory ? (
        <div className="text-xl font-semibold px-4 mb-6 h-10 flex gap-2 items-center">
          <PureIconButtonHelper
            className="py-2 pl-2 pr-2 rounded-full inline-flex items-center leading-6 focus:ring-base hover:text-gray-950 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 focus:ring-offset-0 -ml-2 transition-colors duration-150 ease-out"
            icon={IconsEnum.ARROW_LEFT_OUTLINE}
            iconClassName="h-6 w-6"
            onClick={handleBack}
          />

          <div>{currentCategory.localizedName}</div>
        </div>
      ) : (
        <NextPureLinkHelper
          className="text-xl font-semibold mb-5 flex gap-2 items-center px-4 py-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-150 ease-out"
          i18nText={materialsKeys.singular}
          href={categoryTitleHref}
          disabled={disabledTitleLink}
        />
      )}

      <AlertMessage message={materialCategoriesErrorMessage} />
      <LoadingSkeleton
        loaded={
          !materialCategoriesIsPlaceholderData && materialCategoriesFetched
        }
      >
        <ThreeDStockMenuCategoriesNav
          categories={materialCategoriesWithParents}
          currentCategory={currentCategory}
          // disabled={disabled}
          selectCategory={handleSelectCategory}
          alwaysShowAll={alwaysShowAllCategories}
        />
      </LoadingSkeleton>
    </div>
  );
}

export default ThreeDStockMenuMaterialsCategoriesNav;
